import { Subscription } from 'xstate'

import AudioEngine from '@/webgl/audio/AudioEngine'
import Sound from '@/webgl/audio/Sound'
import Signal from '@/webgl/core/Signal'
import GameWorld from '@/webgl/runner/GameWorld'
import { IRunnerRenderer } from '@/webgl/runner/interfaces/IRunnerRenderer'
import { ScenarioSetup } from '@/webgl/runner/scenarios/ScenarioSetup.types'
import InputSystem from '@/webgl/runner/systems/InputSystem'
import ScrollSystem from '@/webgl/runner/systems/ScrollSystem'
import { IGameSegment } from '@/webgl/runner/systems/SequencingSystem'

type PopinAudio = {
  index: number
  src: string
}

export class Popin implements IGameSegment {
  onComplete = new Signal<void>()

  subscription: Subscription
  scroll: ScrollSystem
  world: GameWorld
  renderer: IRunnerRenderer
  currentAudio: string = null
  currentSound: Sound = null

  constructor(
    readonly id: string,
    readonly dontMove: boolean = false
  ) {}

  init(world: GameWorld, renderer: IRunnerRenderer, setup: ScenarioSetup) {
    this.world = world
    this.scroll = this.world.systems.get<ScrollSystem>(ScrollSystem)
    this.renderer = renderer
  }

  start(): void {
    this.currentAudio = null
    if (import.meta.env.VITE_RUNNER_SKIP_POPIN === 'true') {
      this.onComplete.emit(null)
      return
    }

    this.world.systems.get<InputSystem>(InputSystem).enablePlayerMove(false)

    this.subscription = this.world.state.actor.subscribe((snapshot) => {
      if (snapshot.matches('running')) {
        this.onComplete.emit(null)
        return
      }
      this.audioChange(snapshot.context.game.popinAudio)
    })

    this.world.state.actor.send({ type: 'OPEN_POPIN', value: this.id })
    this.world.state.setPopin(this.id)

    document.addEventListener('keydown', this.onKeyDown)
  }

  audioChange(audio: string) {
    if (audio !== this.currentAudio) {
      this.currentAudio = audio
      this.currentSound?.stop()
      this.currentSound = null

      if (this.currentAudio != null) {
        this.currentSound = AudioEngine.play(this.currentAudio)
        this.currentSound.onEnded.on(this.audioEnded)
      }
    }
  }
  audioEnded = () => {
    this.currentSound = null
    this.world.state.actor.send({ type: 'GAME_STATE', value: { popinAudio: null } })
  }

  onKeyDown = (evt: KeyboardEvent) => {
    if (evt.key == 'Enter') this.world.state.actor.send({ type: 'CLOSE_POPIN' })
  }

  stop(): void {
    this.world.systems.get<InputSystem>(InputSystem).enablePlayerMove(true)
    document.removeEventListener('keydown', this.onKeyDown)

    this.currentAudio = null
    this.currentSound?.stop()
    this.currentSound = null
    this.renderer.eye.open()
    if (!this.dontMove) this.scroll.enable()

    this.world.state.setPopin(null)
    this.subscription?.unsubscribe()
  }
}
